import { EnrollmentApi } from '@/features/enrollment'
import type { EnrollmentData } from '@/features/enrollment'
import { SessionManager } from './sessionManager'
import { buildStepPayload, createClonedEnrollmentData, getInitialEnrollmentData } from './enrollmentDataUtils'

/**
 * Lifecycle management utilities for enrollment wizard
 */
export class LifecycleManager {
  private sessionManager: SessionManager
  private enrollmentDataRef: React.MutableRefObject<EnrollmentData>
  private agentName: string

  constructor(
    sessionManager: SessionManager,
    enrollmentDataRef: React.MutableRefObject<EnrollmentData>,
    agentName: string
  ) {
    this.sessionManager = sessionManager
    this.enrollmentDataRef = enrollmentDataRef
    this.agentName = agentName
  }

  /**
   * Reset enrollment to initial state
   */
  resetEnrollment(
    setEnrollmentData: (data: EnrollmentData) => void,
    setCurrentWizardStepIndex: (index: number) => void,
    setIsCompletedMode: (completed: boolean) => void
  ): void {
    setEnrollmentData(getInitialEnrollmentData(this.agentName))
    setCurrentWizardStepIndex(0)
    setIsCompletedMode(false)
    this.sessionManager.resetSessionState()
  }

  /**
   * Start a fresh enrollment by moving current to pending
   */
  async startNewEnrollment(
    currentWizardStepIndex: number,
    resetEnrollment: () => void,
    setEnrollmentData?: (data: EnrollmentData) => void
  ): Promise<void> {
    try {
      const stepId = this.getStepIdFromIndex(currentWizardStepIndex)
      const snapshot = this.enrollmentDataRef.current
      const stepData = buildStepPayload(stepId, snapshot)

      // ✅ Preserve campaign selection before reset
      const currentCampaign = snapshot.campaign ||
        (snapshot as any).selectedCampaign ||
        (snapshot as any).campaignId

      // Ensure a session exists and save latest progress
      const saved = await EnrollmentApi.saveStepAuto({
        sessionId: this.sessionManager['sessionId'] || undefined,
        stepId,
        stepIndex: currentWizardStepIndex,
        stepData,
      })

      const activeSessionId = saved?.id || this.sessionManager['sessionId']
      if (!activeSessionId) {
        throw new Error('Unable to create or locate a session to move to pending')
      }

      // Move to pending
      await EnrollmentApi.moveToPending(activeSessionId)

      // ✅ Restore campaign after reset
      if (currentCampaign && setEnrollmentData) {
        // Reset enrollment first
        resetEnrollment()

        // Wait a bit for reset to complete
        await new Promise(resolve => setTimeout(resolve, 100))

        // Then restore campaign to enrollment data (both state and ref)
        setEnrollmentData((prev: EnrollmentData) => {
          // Determine campaign object and IDs
          let campaignObj: any = undefined
          let campaignId: string | undefined = undefined
          let selectedCampaign: string | undefined = undefined

          if (typeof currentCampaign === 'object' && currentCampaign !== null) {
            campaignObj = currentCampaign
            campaignId = (currentCampaign as any).id
            selectedCampaign = (currentCampaign as any).name || (currentCampaign as any).id
          } else if (typeof currentCampaign === 'string') {
            campaignId = currentCampaign
            selectedCampaign = currentCampaign
          }

          const updated = {
            ...prev,
            campaign: campaignObj,
            selectedCampaign: selectedCampaign,
            campaignId: campaignId
          } as EnrollmentData

          // Also update the ref
          this.enrollmentDataRef.current = updated

          return updated
        })

        // Also restore campaign ID in session manager
        if (typeof currentCampaign === 'object' && (currentCampaign as any).id) {
          await this.sessionManager.setCampaignId((currentCampaign as any).id)
        } else if (typeof currentCampaign === 'string') {
          await this.sessionManager.setCampaignId(currentCampaign)
        }
      } else {
        // Reset to a brand new enrollment
        resetEnrollment()
      }
    } catch (e) {
      console.error('Failed to move current session to pending', e)
      // Still reset even if there's an error
      resetEnrollment()
    } finally {
      // Notify dashboard/pending list to refresh counts/table
      try { window.dispatchEvent(new Event('pending:updated')) } catch { }
    }
  }

  /**
   * Complete current enrollment and start fresh
   */
  async completeEnrollment(
    currentWizardStepIndex: number,
    isCompletedMode: boolean,
    resetEnrollment: () => void
  ): Promise<void> {
    try {
      await this.sessionManager.completeSession(currentWizardStepIndex)
    } catch (e) {
      console.error('Failed to complete enrollment', e)
    } finally {
      // Only reset to a new enrollment if not in completed mode
      if (!isCompletedMode) {
        resetEnrollment()
      }
      // Notify dashboard/pending list to refresh counts/table
      try { window.dispatchEvent(new Event('pending:updated')) } catch { }
    }
  }

  /**
   * Start next child flow: complete current session and clone data to a new draft starting at Step 3
   */
  async startNextChildFromCurrent(
    currentWizardStepIndex: number,
    setEnrollmentData: (data: EnrollmentData) => void,
    setCurrentWizardStepIndex: (index: number) => void
  ): Promise<void> {
    try {
      const snapshot = this.enrollmentDataRef.current

      // Save ALL step data to ensure complete enrollment data is preserved
      if (this.sessionManager['sessionId']) {
        // Save Step 1 data (platform verification) - CRUCIAL for gamertag tracking
        await EnrollmentApi.saveStepAuto({
          sessionId: this.sessionManager['sessionId'],
          stepId: 'step1',
          stepIndex: 0,
          stepData: buildStepPayload('step1', snapshot),
        })

        // Save Step 2 data (contact and intake)
        await EnrollmentApi.saveStepAuto({
          sessionId: this.sessionManager['sessionId'],
          stepId: 'step2',
          stepIndex: 1,
          stepData: buildStepPayload('step2', snapshot),
        })

        // Save Step 3 data (injured party)
        await EnrollmentApi.saveStepAuto({
          sessionId: this.sessionManager['sessionId'],
          stepId: 'step3',
          stepIndex: 2,
          stepData: buildStepPayload('step3', snapshot),
        })

        // Save Step 4 data (gaming details)
        await EnrollmentApi.saveStepAuto({
          sessionId: this.sessionManager['sessionId'],
          stepId: 'step4',
          stepIndex: 3,
          stepData: buildStepPayload('step4', snapshot),
        })

        // Save Step 5 data (health and legal)
        await EnrollmentApi.saveStepAuto({
          sessionId: this.sessionManager['sessionId'],
          stepId: 'step5',
          stepIndex: 4,
          stepData: buildStepPayload('step5', snapshot),
        })

        // Save Step 6 data (emergency contact)
        await EnrollmentApi.saveStepAuto({
          sessionId: this.sessionManager['sessionId'],
          stepId: 'step6',
          stepIndex: 5,
          stepData: buildStepPayload('step6', snapshot),
        })
      }

      // Mark current session as completed
      if (this.sessionManager['sessionId']) {
        try {
          await EnrollmentApi.complete(this.sessionManager['sessionId'])
        } catch (e) {
          console.warn('Complete session failed', e)
        }
      }

      // Use the current snapshot data directly since we just saved all steps
      let baseData = snapshot

      // Build cloned state: copy ALL data except child-specific identifying information
      const cloned = createClonedEnrollmentData(baseData)

      // Reset to new local state and step 3
      setEnrollmentData(cloned)
      setCurrentWizardStepIndex(2) // Step 3
      this.sessionManager.resetSessionState()

      // Create a new session with the cloned data by calling saveStepAuto without sessionId
      try {
        // Save Step 1 data to create new session
        const newSession = await EnrollmentApi.saveStepAuto({
          sessionId: undefined, // This will create a new session
          stepId: 'step1',
          stepIndex: 0,
          stepData: buildStepPayload('step1', cloned),
        })

        if (newSession?.id) {
          this.sessionManager['setSessionId'](newSession.id)
          this.sessionManager['saveSessionIdToStorage'](newSession.id)

          // Save Step 2 data to the new session
          await EnrollmentApi.saveStepAuto({
            sessionId: newSession.id,
            stepId: 'step2',
            stepIndex: 1,
            stepData: buildStepPayload('step2', cloned),
          })
        }
      } catch (e) {
        console.error('Failed to create new session with cloned data:', e)
      }
    } catch (e) {
      console.error('Failed to start next child flow', e)
    }
  }

  /**
   * Generate QR code (mock implementation)
   */
  generateQRCode(): void {
    // In a real app, this would generate a QR code with a unique URL
    alert('QR Code generated! Client can scan with their phone to access the enrollment form.')
  }

  /**
   * Send email link to client
   */
  async sendEmailLink(clientEmail: string): Promise<void> {
    try {
      await this.sessionManager.sendEmailLink(clientEmail)
      // Success - no alert needed, UI will handle feedback
    } catch (e) {
      console.error('Failed to send email link', e)
      throw e // Re-throw to let the UI handle the error
    }
  }

  /**
   * Get step ID from step index
   */
  private getStepIdFromIndex(stepIndex: number): string {
    const stepIds = Array.from({ length: 20 }, (_, i) => `step${i + 1}`)
    return stepIds[stepIndex] || 'step1'
  }
}
